/**
 * Draft Order Lock Manager
 * Manages the locking state of draft orders to prevent webhook interference during checkout
 * 
 * CONCEPT:
 * - Unlocked: Webhooks can freely update draft order
 * - Locked: Only API endpoints can update draft order, webhooks are ignored
 * - Lock is set when checkout process begins
 * - Lock is cleared when checkout is completed or cancelled
 */

class DraftOrderLockManager {
  constructor() {
    this.lockAttribute = 'draft_order_locked';
    this.lockTimestampAttribute = 'draft_order_locked_at';
    this.lockReasonAttribute = 'draft_order_lock_reason';
    
    // Maximum lock duration (30 minutes) to prevent permanent locks
    this.maxLockDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
    
    this.initializeLockManager();
  }

  /**
   * Get cart headers with proper fallbacks
   */
  getHeaders() {
    if (window.createWebhookSafeHeaders) {
      return window.createWebhookSafeHeaders();
    } else if (window.getCartHeaders) {
      return window.getCartHeaders();
    } else {
      // Minimal fallback headers
      return {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
    }
  }

  initializeLockManager() {
    console.log('🔒 Initializing Draft Order Lock Manager...');
    
    // Listen for checkout events
    document.addEventListener('checkout:started', (e) => this.handleCheckoutStarted(e.detail));
    document.addEventListener('checkout:completed', (e) => this.handleCheckoutCompleted(e.detail));
    document.addEventListener('checkout:cancelled', (e) => this.handleCheckoutCancelled(e.detail));
    document.addEventListener('checkout:timeout', (e) => this.handleCheckoutTimeout(e.detail));
    
    // Check for expired locks on page load
    // this.checkForExpiredLocks();
    
    console.log('✅ Draft Order Lock Manager initialized');
  }

  /**
   * Check if draft order is currently locked
   */
  async isDraftOrderLocked() {
    try {
      const cartResponse = await fetch('/cart.json', {
        headers: this.getHeaders()
      });
      
      const cart = await cartResponse.json();
      const isLocked = cart.attributes[this.lockAttribute] === 'true';
      
      if (isLocked) {
        // Check if lock has expired
        const lockTimestamp = parseInt(cart.attributes[this.lockTimestampAttribute] || '0');
        const lockAge = Date.now() - lockTimestamp;
        
        if (lockAge > this.maxLockDuration) {
          console.warn('🔒 Draft order lock has expired, automatically unlocking');
          await this.unlockDraftOrder('expired');
          return false;
        }
        
        console.log(`🔒 Draft order is locked (${Math.round(lockAge / 1000)}s ago)`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Failed to check draft order lock status:', error);
      // Assume unlocked on error to avoid blocking operations
      return false;
    }
  }

  /**
   * Lock the draft order (prevent webhook updates)
   */
  async lockDraftOrder(reason = 'checkout_started') {
    try {
      console.log(`🔒 Locking draft order (reason: ${reason})...`);
      
      const formData = new FormData();
      formData.append('attributes[locked]', 'true');
      formData.append(`attributes[${this.lockAttribute}]`, 'true');
      formData.append(`attributes[${this.lockTimestampAttribute}]`, Date.now().toString());
      formData.append(`attributes[${this.lockReasonAttribute}]`, reason);
      
      const expectedAttrs = {
          customer_id: window.customer.id.toString(),
          user_name: window.customer.userName,
          user_number: window.customer.currentUserNumber.toString(),
          checkout: true
      };

      // Create user identification string for note field
      // Format: customer_id|user_name|user_number|checkout|actual_note
      const userIdentifier = `${expectedAttrs.customer_id}|${expectedAttrs.user_name}|${expectedAttrs.user_number}|${expectedAttrs.checkout}`;
      
      // Extract actual note if it exists (everything after the third pipe)
      let actualNote = '';
      if (cart.note) {
          const noteParts = cart.note.split('|');
          if (noteParts.length > 4) {
              // Note already contains our format, extract actual note
              actualNote = noteParts.slice(4).join('|');
          } else if (!cart.note.includes('|') || noteParts.length < 4) {
              // Preserve existing note if it doesn't contain our format
              actualNote = cart.note;
          }
      }
      
      // Combine user identifier with actual note
      const fullNote = actualNote ? `${userIdentifier}|${actualNote}` : userIdentifier;

      formData.append('note', fullNote);

      const headers = this.getHeaders();
      // Remove Content-Type for FormData
      delete headers['Content-Type'];
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: headers,
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Lock request failed: ${response.status}`);
      }
      
      // Set local flag for immediate use
      this.setLocalLockFlag(true);
      
      // Dispatch lock event
      document.dispatchEvent(new CustomEvent('draft-order:locked', {
        detail: { reason, timestamp: Date.now() }
      }));
      
      console.log(`✅ Draft order locked successfully (reason: ${reason})`);
      return true;
      
    } catch (error) {
      console.error('❌ Failed to lock draft order:', error);
      return false;
    }
  }

  /**
   * Unlock the draft order (allow webhook updates)
   */
  async unlockDraftOrder(reason = 'checkout_completed') {
    try {
      console.log(`🔓 Unlocking draft order (reason: ${reason})...`);
      
      const formData = new FormData();
      formData.append('attributes[locked]', '');
      formData.append(`attributes[${this.lockAttribute}]`, '');
      formData.append(`attributes[${this.lockTimestampAttribute}]`, '');
      formData.append(`attributes[${this.lockReasonAttribute}]`, '');
      
      const expectedAttrs = {
          customer_id: window.customer.id.toString(),
          user_name: window.customer.userName,
          user_number: window.customer.currentUserNumber.toString(),
          checkout: false
      };

      // Create user identification string for note field
      // Format: customer_id|user_name|user_number|checkout|actual_note
      const userIdentifier = `${expectedAttrs.customer_id}|${expectedAttrs.user_name}|${expectedAttrs.user_number}|${expectedAttrs.checkout}`;
      
      // Extract actual note if it exists (everything after the third pipe)
      let actualNote = '';
      if (cart.note) {
          const noteParts = cart.note.split('|');
          if (noteParts.length > 4) {
              // Note already contains our format, extract actual note
              actualNote = noteParts.slice(4).join('|');
          } else if (!cart.note.includes('|') || noteParts.length < 4) {
              // Preserve existing note if it doesn't contain our format
              actualNote = cart.note;
          }
      }
      
      // Combine user identifier with actual note
      const fullNote = actualNote ? `${userIdentifier}|${actualNote}` : userIdentifier;

      formData.append('note', fullNote);

      const headers = this.getHeaders();
      // Remove Content-Type for FormData
      delete headers['Content-Type'];
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: headers,
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Unlock request failed: ${response.status}`);
      }
      
      // Clear local flag
      this.setLocalLockFlag(false);
      
      // Dispatch unlock event
      document.dispatchEvent(new CustomEvent('draft-order:unlocked', {
        detail: { reason, timestamp: Date.now() }
      }));
      
      console.log(`✅ Draft order unlocked successfully (reason: ${reason})`);
      return true;
      
    } catch (error) {
      console.error('❌ Failed to unlock draft order:', error);
      return false;
    }
  }

  /**
   * Set local lock flag for immediate use (before server sync)
   */
  setLocalLockFlag(isLocked) {
    if (isLocked) {
      sessionStorage.setItem('draft_order_locked', 'true');
      sessionStorage.setItem('draft_order_locked_at', Date.now().toString());
    } else {
      sessionStorage.removeItem('draft_order_locked');
      sessionStorage.removeItem('draft_order_locked_at');
    }
  }

  /**
   * Get local lock status (for immediate checks)
   */
  isLocallyLocked() {
    const isLocked = sessionStorage.getItem('draft_order_locked') === 'true';
    
    if (isLocked) {
      // Check for expired local lock
      const lockTimestamp = parseInt(sessionStorage.getItem('draft_order_locked_at') || '0');
      const lockAge = Date.now() - lockTimestamp;
      
      if (lockAge > this.maxLockDuration) {
        console.warn('🔒 Local draft order lock has expired, clearing');
        this.setLocalLockFlag(false);
        return false;
      }
    }
    
    return isLocked;
  }

  /**
   * Handle checkout started event
   */
  async handleCheckoutStarted(detail = {}) {
    console.log('🛒 Checkout started, locking draft order...');
    await this.lockDraftOrder('checkout_started');
  }

  /**
   * Handle checkout completed event
   */
  async handleCheckoutCompleted(detail = {}) {
    console.log('✅ Checkout completed, unlocking draft order...');
    await this.unlockDraftOrder('checkout_completed');
  }

  /**
   * Handle checkout cancelled event
   */
  async handleCheckoutCancelled(detail = {}) {
    console.log('❌ Checkout cancelled, unlocking draft order...');
    await this.unlockDraftOrder('checkout_cancelled');
  }

  /**
   * Handle checkout timeout event
   */
  async handleCheckoutTimeout(detail = {}) {
    console.log('⏰ Checkout timeout, unlocking draft order...');
    await this.unlockDraftOrder('checkout_timeout');
  }

  /**
   * Check for and clear expired locks on page load
   */
  async checkForExpiredLocks() {
    try {
      const isLocked = await this.isDraftOrderLocked();
      // The isDraftOrderLocked method already handles expired locks
      if (isLocked) {
        console.log('🔒 Draft order is currently locked');
      }
    } catch (error) {
      console.warn('⚠️ Could not check for expired locks:', error);
    }
  }

  /**
   * Force unlock (emergency function)
   */
  async forceUnlock(reason = 'force_unlock') {
    console.warn(`🚨 Force unlocking draft order (reason: ${reason})`);
    return await this.unlockDraftOrder(reason);
  }

  /**
   * Get lock status info
   */
  async getLockInfo() {
    try {
      const cartResponse = await fetch('/cart.json', {
        headers: this.getHeaders()
      });
      
      const cart = await cartResponse.json();
      const isLocked = cart.attributes[this.lockAttribute] === 'true';
      
      if (isLocked) {
        const lockTimestamp = parseInt(cart.attributes[this.lockTimestampAttribute] || '0');
        const lockReason = cart.attributes[this.lockReasonAttribute] || 'unknown';
        const lockAge = Date.now() - lockTimestamp;
        
        return {
          locked: true,
          timestamp: lockTimestamp,
          reason: lockReason,
          age: lockAge,
          ageString: this.formatLockAge(lockAge)
        };
      }
      
      return { locked: false };
    } catch (error) {
      console.error('❌ Failed to get lock info:', error);
      return { locked: false, error: error.message };
    }
  }

  /**
   * Format lock age for display
   */
  formatLockAge(ageMs) {
    const seconds = Math.floor(ageMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }
}

// Initialize the draft order lock manager
window.draftOrderLockManager = new DraftOrderLockManager();

// Export utility functions for other modules
window.isDraftOrderLocked = () => {
  return window.draftOrderLockManager.isLocallyLocked() || 
         window.draftOrderLockManager.isDraftOrderLocked();
};

window.lockDraftOrder = (reason) => {
  return window.draftOrderLockManager.lockDraftOrder(reason);
};

window.unlockDraftOrder = (reason) => {
  return window.draftOrderLockManager.unlockDraftOrder(reason);
};

window.getDraftOrderLockInfo = () => {
  return window.draftOrderLockManager.getLockInfo();
};

console.log('✅ Draft Order Lock Manager loaded');
console.log('🔧 Available functions: isDraftOrderLocked(), lockDraftOrder(reason), unlockDraftOrder(reason)');